import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Hash, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';

interface AccountNumberChangeFormProps {
  workspaceId: string;
  projectId?: string;
  onSuccess?: () => void;
}

export function AccountNumberChangeForm({ workspaceId, projectId, onSuccess }: AccountNumberChangeFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    account_id: '',
    old_account_number: '',
    new_account_number: '',
    effective_date: '',
    preserve_history: true,
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, account_number')
        .eq('workspace_id', workspaceId)
        .eq('status', 'active')
        .order('account_number');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspaceId,
  });

  useEffect(() => {
    if (formData.account_id && accounts) {
      const account = accounts.find((a: any) => a.id === formData.account_id);
      if (account) {
        setFormData(prev => ({ ...prev, old_account_number: account.account_number }));
      }
    }
  }, [formData.account_id, accounts]);

  const createRequestMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const requestData = {
        account_id: formData.account_id,
        old_account_number: formData.old_account_number,
        new_account_number: formData.new_account_number,
        effective_date: formData.effective_date,
        preserve_history: formData.preserve_history,
      };

      const { data, error } = await supabase
        .from('tum_change_management_requests')
        .insert({
          workspace_id: workspaceId,
          onboarding_project_id: projectId || null,
          request_type: 'account_number_change',
          request_data: requestData,
          description: formData.description,
          priority: formData.priority,
          requested_effective_date: formData.effective_date || null,
          requested_by: user.id,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-management-counts', workspaceId] });
      toast({
        title: 'Account change request submitted',
        description: 'Your request has been submitted and is pending approval.',
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error submitting request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.account_id) {
      toast({
        title: 'Account required',
        description: 'Please select an account',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.new_account_number.trim()) {
      toast({
        title: 'New account number required',
        description: 'Please enter the new account number',
        variant: 'destructive',
      });
      return;
    }

    if (formData.new_account_number === formData.old_account_number) {
      toast({
        title: 'Account numbers must differ',
        description: 'The new account number must be different from the old one',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.effective_date) {
      toast({
        title: 'Effective date required',
        description: 'Please select an effective date',
        variant: 'destructive',
      });
      return;
    }

    createRequestMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Hash className="h-5 w-5" />
          Account Number Change Request
        </CardTitle>
        <CardDescription>
          Change an account number from the old number to a new number. Historical data can be preserved.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="account">Account *</Label>
            <Select
              value={formData.account_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, account_id: value }))}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts?.map((account: any) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.account_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="oldAccountNumber">Old Account Number</Label>
              <Input
                id="oldAccountNumber"
                value={formData.old_account_number}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newAccountNumber">New Account Number *</Label>
              <Input
                id="newAccountNumber"
                value={formData.new_account_number}
                onChange={(e) => setFormData(prev => ({ ...prev, new_account_number: e.target.value }))}
                placeholder="Enter new account number"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="effectiveDate">Effective Date *</Label>
            <Input
              id="effectiveDate"
              type="date"
              value={formData.effective_date}
              onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
              required
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label htmlFor="preserveHistory">Preserve History</Label>
              <p className="text-xs text-muted-foreground">
                Keep historical bills and data linked to the old account number
              </p>
            </div>
            <Switch
              id="preserveHistory"
              checked={formData.preserve_history}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, preserve_history: checked }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description / Notes</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Additional information about this change..."
              rows={3}
            />
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This will change the account number in the system. Make sure the new account number is correct.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button 
              type="submit"
              disabled={createRequestMutation.isPending}
              className="flex-1"
            >
              {createRequestMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

